import { useEffect, useState } from "react"
import { Button } from "@/components/ui/button"
import { cn } from "@/lib/utils"
import { ChevronDown, ChevronUp, Loader2 } from "lucide-react"
import * as outreachApi from "../api/outreach.api"
import { formatInTz } from "../lib/time"
import type { ReliabilityReport, ReliabilityRow, UnprocessedText } from "../types/outreach.types"

type Kind = "missed" | "late" | "skipped" | "unreconciled" | "unprocessed_texts"

const KINDS: { key: Kind; label: string; help: string }[] = [
  { key: "missed", label: "Missed", help: "Booked but never fired (more than 20 min past due)" },
  { key: "late", label: "Late", help: "Call started more than 20 min after the booked time" },
  { key: "skipped", label: "Skipped", help: "Not placed at fire time (pause, policy, or patient state)" },
  { key: "unreconciled", label: "Unreconciled", help: "Placed, but no call outcome came back" },
  {
    key: "unprocessed_texts",
    label: "Unprocessed texts",
    help: "Patient texts we failed to process. Each one is in the reply inbox; a person must read and answer it",
  },
]

/** A table row for either a scheduled call/text or an unprocessed inbound text. */
interface Row {
  key: string
  patientId: string | null
  name: string
  when: string
  detail: string
}

function channelWord(r: ReliabilityRow) {
  return r.channel === "SMS" ? "Text" : "Call"
}

function rowWhy(r: ReliabilityRow) {
  if (r.minutes_late != null) return `${channelWord(r)} · ${Math.round(r.minutes_late)} min late`
  if (r.call_status) return `${channelWord(r)} · no outcome (${r.call_status.toLowerCase().replace(/_/g, " ")})`
  const why = r.skip_reason || r.status
  return why ? `${channelWord(r)} · ${String(why).replace(/_/g, " ")}` : channelWord(r)
}

function nameOf(firstName: string | null | undefined, patientId: string | null | undefined): string {
  if (firstName) return String(firstName)
  return patientId ? String(patientId).slice(0, 8) : "Unknown"
}

function toRows(report: ReliabilityReport, kind: Kind): Row[] {
  if (kind === "unprocessed_texts") {
    return (report.unprocessed_texts || []).map((t: UnprocessedText, i) => ({
      key: t.message_sid || String(i),
      patientId: t.patient_id,
      name: nameOf(t.patient_first_name, t.patient_id),
      when: formatInTz(t.received_at || null),
      detail: "Text received, not processed: see the reply inbox",
    }))
  }
  return (report[kind] || []).map((r, i) => ({
    key: r.id || String(i),
    patientId: r.patient_id,
    name: nameOf(r.patient_first_name, r.patient_id),
    when: formatInTz(r.scheduled_at || null),
    detail: rowWhy(r),
  }))
}

/**
 * GET /outreach/org/{org}/reliability. The report is clinic-wide, not
 * per-campaign; it says so in the header.
 */
export function ReliabilityStrip({ orgId, days = 7, onOpenPatient }: { orgId: string; days?: number; onOpenPatient?: (patientId: string) => void }) {
  const [report, setReport] = useState<ReliabilityReport | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [open, setOpen] = useState<Kind | null>(null)

  useEffect(() => {
    let cancelled = false
    setError(null)
    outreachApi
      .getReliability(orgId, days)
      .then((r) => !cancelled && setReport(r))
      .catch((err) => !cancelled && setError(outreachApi.errorMessage(err)))
    return () => {
      cancelled = true
    }
  }, [orgId, days])

  if (error) {
    return <p className="rounded-xl border px-4 py-3 text-sm text-muted-foreground">Reliability report unavailable: {error}</p>
  }
  if (!report) {
    return (
      <div className="flex items-center gap-2 rounded-xl border px-4 py-3 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading reliability…
      </div>
    )
  }

  const count = (k: Kind) => report.counts?.[k] ?? report[k]?.length ?? 0
  const total = KINDS.reduce((n, k) => n + count(k.key), 0)
  const rows = open ? toRows(report, open) : []

  return (
    <div className={cn("rounded-xl border", total > 0 ? "border-warning/40" : "")}>
      <div className="flex flex-col gap-2 px-4 py-3 md:flex-row md:items-center md:justify-between">
        <p className="text-sm font-medium">
          Delivery reliability, last {days} days
          <span className="ml-1 text-xs font-normal text-muted-foreground">(whole clinic)</span>
        </p>
        <div className="flex flex-wrap gap-1.5">
          {KINDS.map(({ key, label, help }) => {
            const n = count(key)
            return (
              <Button
                key={key}
                size="sm"
                variant="outline"
                title={help}
                disabled={n === 0}
                onClick={() => setOpen(open === key ? null : key)}
                className={cn("gap-1.5", n > 0 && "border-warning/40 text-warning hover:text-warning", open === key && "bg-warning/10")}
              >
                {label} <span className="tabular-nums font-semibold">{n}</span>
                {n > 0 && (open === key ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />)}
              </Button>
            )
          })}
        </div>
      </div>
      {open && (
        <div className="max-h-72 overflow-y-auto border-t">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-card text-left text-muted-foreground">
              <tr>
                <th className="px-4 py-1.5 font-medium">Patient</th>
                <th className="px-4 py-1.5 font-medium">{open === "unprocessed_texts" ? "Received" : "Booked for"}</th>
                <th className="px-4 py-1.5 font-medium">Detail</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.key} className="border-t">
                  <td className="px-4 py-1.5">
                    {r.patientId && onOpenPatient ? (
                      <button className="hover:underline" onClick={() => onOpenPatient(String(r.patientId))}>
                        {r.name}
                      </button>
                    ) : (
                      r.name
                    )}
                  </td>
                  <td className="px-4 py-1.5 whitespace-nowrap">{r.when}</td>
                  <td className="px-4 py-1.5">{r.detail}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
